import React, { createContext, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { message, Button } from 'antd';
import { Formik, Form } from 'formik';

import { BottomBar, FormDebug } from 'components';
import { IBuilderSaveResult } from 'components/EmbeddedBuilder';
import FormikUnsavedChangesGuard from 'components/FormikUnsavedChangesGuard';
import FullscreenOverlay from 'components/FullscreenOverlay';
import { PageContent } from 'components/page';
import { useTypedSelector } from 'hooks';
import { getIncompleteFiltersMessage, sanitizeFilters } from 'utils/audience';
import API from 'utils/request';
import serializeFormData from 'utils/serializeFormData';

import EmbeddedAudienceSkin from './EmbeddedAudienceSkin';
import { AudienceEmbeddedContext, AudienceLockedAttributesContext } from './lockContext';
import { Audience } from './steps';
import { validationSchema } from './validationSchema';

const defaultValues = {
  name: '',
  user_groups: [],
  filters: {},
  auto_create_customers: false
};

export const FormContext = createContext({ isEditMode: false, locale: 'en' });

export interface IAudienceFormEmbedded {
  /** PUT target in edit mode; the route has no id outside the audiences pages. */
  audienceId?: number;
  onSaved: (result: IBuilderSaveResult) => void;
  onCancel: () => void;
  hideUpload?: boolean;
  /** Custom-attribute keys set by the host that the user may not edit or remove. */
  lockedCustomAttributes?: string[];
}

interface AudienceFormProps {
  initialValues?: any;
  isEditMode?: boolean;
  /** Runs the form inside a host: no navigation, the host handles save and cancel. */
  embedded?: IAudienceFormEmbedded;
}

const NO_LOCKED_ATTRIBUTES: string[] = [];

const AudienceForm = ({ initialValues = {}, isEditMode = false, embedded }: AudienceFormProps) => {
  const navigate = useNavigate();
  const routeParams = useParams<{ id: string }>();
  const audienceId = embedded ? embedded.audienceId : routeParams.id;
  const selectedLocale = useTypedSelector(state => state.global?.selectedLocale);
  const toastKey = 'audience-submit';

  const onSubmit = async values => {
    const incompleteFiltersMessage = getIncompleteFiltersMessage(values.filters);
    if (incompleteFiltersMessage) {
      message.error(incompleteFiltersMessage);
      return;
    }
    try {
      message.loading({ content: 'Submitting Data', duration: 0, key: toastKey });
      const nonEmptyFilters = sanitizeFilters(values.filters);

      const cleanValues = {
        // _payload is used for more convenient and reliable submitting nested parts of FormData.
        // The properties containing files (like user_groups in this case)
        // shouldn't be included into _payload
        _payload: JSON.stringify({
          name: values.name,
          filters: nonEmptyFilters,
          auto_create_customers: values.auto_create_customers
        }),
        user_groups: values.user_groups.map(group => ({
          ...group,
          id: group.id || null,
          file: group.file || null
        }))
      };

      const formData = serializeFormData(cleanValues);

      if (isEditMode) {
        await API.put(`/v4/dash/v4_audiences/${audienceId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        message.success({ content: 'File Updated Success', key: toastKey });
        if (embedded) {
          embedded.onSaved({ entity: 'audience', id: Number(audienceId) });
          return;
        }
        navigate(`/p/audiences/show/${audienceId}`);
      } else {
        const response = await API.post(`/v4/dash/v4_audiences`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const id = response?.data?.data.id;

        message.success({ content: 'File Upload Success', key: toastKey });
        if (embedded) {
          embedded.onSaved({ entity: 'audience', id });
          return;
        }
        navigate(id ? `/p/audiences/show/${id}` : `/p/audiences/show/list`);
      }
    } catch (error) {
      const responseData = error?.response?.data;
      const apiMessage =
        responseData?.message ||
        responseData?.error ||
        (Array.isArray(responseData?.errors) ? responseData.errors.join(', ') : null);
      const fallbackMessage = isEditMode
        ? 'Error updating the Audience.'
        : 'Error creating the Audience.';
      const messageContent = apiMessage || error?.message || fallbackMessage;
      if (isEditMode) {
        message.error({ content: messageContent, key: toastKey });
      } else {
        message.error({ content: messageContent, key: toastKey });
      }
    }
  };

  const mergedInitialValues = useMemo(
    () => ({ ...defaultValues, ...initialValues }),
    [initialValues]
  );

  const FormBody = embedded ? EmbeddedAudienceSkin : PageContent;

  return (
    <FormContext.Provider value={{ isEditMode, locale: selectedLocale }}>
      <AudienceLockedAttributesContext.Provider
        value={embedded?.lockedCustomAttributes ?? NO_LOCKED_ATTRIBUTES}
      >
        <AudienceEmbeddedContext.Provider value={Boolean(embedded)}>
          <FormBody>
            <Formik
              validationSchema={validationSchema}
              onSubmit={onSubmit}
              initialValues={mergedInitialValues}
              validateOnChange
            >
              {({ isValid, isSubmitting }) => (
                <Form>
                  {!embedded && <FormikUnsavedChangesGuard />}
                  {!embedded && (
                    <FullscreenOverlay active={isSubmitting} testId="audience-submit-overlay" />
                  )}
                  <Audience
                    isEditMode={isEditMode}
                    hideUpload={embedded?.hideUpload}
                    essentialsTitle={
                      embedded ? (isEditMode ? 'Edit Audience' : 'Create Audience') : undefined
                    }
                  />
                  {!embedded && process.env.NODE_ENV === 'development' && <FormDebug />}
                  <BottomBar placement={embedded ? 'inline' : 'fixed'}>
                    <Button
                      size="large"
                      type="default"
                      onClick={() => (embedded ? embedded.onCancel() : navigate(-1))}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="large"
                      type="primary"
                      htmlType="submit"
                      disabled={!isValid || isSubmitting}
                      loading={isSubmitting}
                      style={{ marginLeft: 8 }}
                    >
                      {isEditMode ? 'Update' : 'Create'}
                    </Button>
                  </BottomBar>
                </Form>
              )}
            </Formik>
          </FormBody>
        </AudienceEmbeddedContext.Provider>
      </AudienceLockedAttributesContext.Provider>
    </FormContext.Provider>
  );
};

export default AudienceForm;
